import React, { useEffect, useState } from "react";
import { readCache, writeCache } from "../dataCache";
import MobileScrollArea from "../components/MobileScrollArea";

const API_URL = window.location.origin;

const emptyForm = {
  provider: "gmail",
  senderName: "",
  senderEmail: "",
  host: "smtp.gmail.com",
  port: 587,
  secure: false,
  user: "",
  pass: "",
  dailyLimit: 100,
  active: true
};

const PROVIDER_PRESETS = {
  gmail: { label: "Gmail / Google Workspace", host: "smtp.gmail.com", port: 587, secure: false },
  outlook: { label: "Outlook / Microsoft 365", host: "smtp.office365.com", port: 587, secure: false },
  zoho: { label: "Zoho Mail", host: "smtp.zoho.com", port: 587, secure: false },
  yahoo: { label: "Yahoo Mail", host: "smtp.mail.yahoo.com", port: 587, secure: false },
  hostinger: { label: "Hostinger", host: "smtp.hostinger.com", port: 465, secure: true },
  amazonses: { label: "Amazon SES (best inbox delivery)", host: "email-smtp.us-east-1.amazonaws.com", port: 465, secure: true },
  custom: { label: "Custom / My Domain SMTP", host: "", port: 587, secure: false }
};

function detectProvider(host) {
  const h = String(host || "").toLowerCase();
  if (h.includes("gmail") || h.includes("googlemail")) return "gmail";
  if (h.includes("office365") || h.includes("outlook")) return "outlook";
  if (h.includes("zoho")) return "zoho";
  if (h.includes("yahoo")) return "yahoo";
  if (h.includes("hostinger")) return "hostinger";
  if (h.includes("amazonaws")) return "amazonses";
  return "custom";
}

// Amazon SES SMTP endpoints are region-specific; the host is always
// email-smtp.<region>.amazonaws.com. These are the common SES regions.
const SES_REGIONS = [
  "us-east-1", "us-east-2", "us-west-1", "us-west-2",
  "eu-west-1", "eu-west-2", "eu-central-1", "eu-north-1",
  "ap-south-1", "ap-southeast-1", "ap-southeast-2", "ap-northeast-1",
  "ca-central-1", "sa-east-1", "me-south-1"
];

export default function SMTPPage() {
  const [smtps, setSmtps] = useState(() => readCache("smtps", []));
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [showPass, setShowPass] = useState(false);

  async function loadSmtps() {
    try {
      const res = await fetch(`${API_URL}/api/smtp`);
      const data = await res.json();
      const nextSmtps = Array.isArray(data) ? data : [];
      setSmtps(nextSmtps);
      writeCache("smtps", nextSmtps);
    } catch (err) {
      const isConnErr = /unexpected token|json|failed to fetch|networkerror|<html/i.test(String((err && err.message) || "")); console.warn("SMTP load failed:", err); alert(isConnErr ? "Couldn't load SMTP settings. The server may be starting up or is temporarily unavailable \u2014 please refresh the page in a moment." : ("Couldn't load SMTP settings: " + (err && err.message)));
    }
  }

  useEffect(() => {
    loadSmtps();
  }, []);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function applyProvider(providerKey) {
    const preset = PROVIDER_PRESETS[providerKey] || PROVIDER_PRESETS.custom;
    setForm((prev) => ({
      ...prev,
      provider: providerKey,
      host: providerKey === "custom" ? prev.host : preset.host,
      port: preset.port,
      secure: preset.secure
    }));
  }

  async function saveSmtp() {
    if (!form.senderName || !form.senderEmail || !form.host || !form.user || (!editingId && !form.pass)) {
      alert("Please fill all required SMTP fields.");
      return;
    }

    try {
      setLoading(true);

      const url = editingId
        ? `${API_URL}/api/smtp/${editingId}`
        : `${API_URL}/api/smtp`;

      const method = editingId ? "PATCH" : "POST";

      const payload = { ...form };
      if (editingId && !payload.pass) delete payload.pass;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.message || "SMTP save failed.");
        return;
      }

      alert(data.message || "SMTP saved successfully.");
      setForm(emptyForm);
      setEditingId(null);
      await loadSmtps();
    } catch (err) {
      alert("SMTP save error: " + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function editSmtp(smtp) {
    setEditingId(smtp._id);
    setShowPass(false);
    setForm({
      provider: detectProvider(smtp.host),
      senderName: smtp.senderName || smtp.name || "",
      senderEmail: smtp.senderEmail || "",
      host: smtp.host || "",
      port: smtp.port || 587,
      secure: !!smtp.secure,
      user: smtp.user || "",
      pass: "",
      dailyLimit: smtp.dailyLimit || 100,
      active: smtp.active !== false
    });

    window.scrollTo({ top: 0, behavior: "smooth" });

    // Password alag endpoint se aata hai. Na mile to purana behaviour chalta
    // rahega: field khaali, aur save par purana password waisa hi rehta hai.
    try {
      const res = await fetch(`${API_URL}/api/smtp/${smtp._id}/reveal`);
      const data = await res.json();
      if (res.ok && typeof data.pass === "string" && data.pass) {
        setForm((prev) => ({ ...prev, pass: data.pass }));
      }
    } catch (err) {
      console.warn("Password load nahi hua:", err);
    }
  }

  async function deleteSmtp(id) {
    if (!window.confirm("Delete this SMTP? Linked sender will also be removed.")) return;

    try {
      const res = await fetch(`${API_URL}/api/smtp/${id}`, {
        method: "DELETE"
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.message || "Delete failed.");
        return;
      }

      alert(data.message || "SMTP deleted.");
      await loadSmtps();
    } catch (err) {
      alert("Delete error: " + err.message);
    }
  }

  async function testSmtp(id) {
    const to = testEmail || prompt("Enter test email address");
    if (!to) return;

    try {
      const res = await fetch(`${API_URL}/api/smtp/${id}/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to })
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.message || data.error || "SMTP test failed.");
        return;
      }

      alert(data.message || "Test email sent.");
    } catch (err) {
      alert("SMTP test error: " + err.message);
    }
  }

  return (
    <section className="page-card smtp-page">
      <div className="title-row">
        <div>
          <h2>SMTPs</h2>
          <p className="muted smtp-subtitle">
            Add multiple SMTP accounts. Every SMTP will automatically create a sender
            and appear in campaign sender dropdown.
          </p>
        </div>

        <span className="badge">Multiple Senders</span>
      </div>

      <div className="smtp-card">
        <div className="smtp-card-head">
          <div>
            <h3>{editingId ? "Edit SMTP" : "Add New SMTP"}</h3>
            <p>Connect sender email with its own SMTP credentials.</p>
          </div>
        </div>

        <div className="smtp-form-grid">
          <div className="smtp-field smtp-field--full">
            <label>Email Provider</label>
            <select
              className="smtp-provider-select"
              value={form.provider}
              onChange={(e) => applyProvider(e.target.value)}
            >
              {Object.entries(PROVIDER_PRESETS).map(([key, prov]) => (
                <option key={key} value={key}>{prov.label}</option>
              ))}
            </select>
            <p className="smtp-hint">
              Choose <strong>Gmail</strong> to send from Google, or <strong>Custom / My Domain SMTP</strong> to send from your own domain email. Host &amp; port fill in automatically — you can add both and pick them per campaign.
            </p>
            {form.provider === "amazonses" && (
              <div className="smtp-ses-box">
                <div className="smtp-ses-region">
                  <label>AWS Region</label>
                  <select
                    value={(String(form.host).match(/email-smtp\.([a-z0-9-]+)\.amazonaws/) || [])[1] || "us-east-1"}
                    onChange={(e) => updateField("host", `email-smtp.${e.target.value}.amazonaws.com`)}
                  >
                    {SES_REGIONS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div className="smtp-ses-help">
                  <strong>Amazon SES setup (one time):</strong>
                  <ol>
                    <li>In the AWS Console open <strong>Amazon SES</strong> and select the region chosen above.</li>
                    <li><strong>Verify your domain</strong> (SES &rarr; Verified identities &rarr; Create identity &rarr; Domain). SES gives you 3 DKIM CNAME records — add them in your DNS (Hostinger).</li>
                    <li>Add <code>include:amazonses.com</code> to your domain&apos;s SPF TXT record.</li>
                    <li>Request <strong>production access</strong> to leave the SES sandbox, so you can mail any recipient.</li>
                    <li>Open <strong>SMTP settings &rarr; Create SMTP credentials</strong>, then paste the generated username &amp; password below.</li>
                  </ol>
                  <p>Sender Email must be an address on your verified domain (e.g. <code>info1@emailx.alinag.online</code>). Port 465 (SSL) is pre-filled.</p>
                </div>
              </div>
            )}
          </div>
          <div className="smtp-field">
            <label>Sender Name *</label>
            <input
              value={form.senderName}
              onChange={(e) => updateField("senderName", e.target.value)}
              placeholder="Example: Alina"
            />
          </div>

          <div className="smtp-field">
            <label>Sender Email *</label>
            <input
              value={form.senderEmail}
              onChange={(e) => updateField("senderEmail", e.target.value)}
              placeholder="sender@gmail.com"
            />
          </div>

          <div className="smtp-field">
            <label>SMTP Host *</label>
            <input
              value={form.host}
              onChange={(e) => updateField("host", e.target.value)}
              placeholder="smtp.gmail.com"
            />
          </div>

          <div className="smtp-field">
            <label>SMTP Port *</label>
            <input
              type="number"
              value={form.port}
              onChange={(e) => updateField("port", Number(e.target.value))}
            />
          </div>

          <div className="smtp-field">
            <label>SMTP Username *</label>
            <input
              value={form.user}
              onChange={(e) => updateField("user", e.target.value)}
              placeholder="smtp login email"
            />
          </div>

          <div className="smtp-field">
            <label>
              SMTP Password / App Password {!editingId && "*"}
            </label>
            <div className="smtp-pass-wrap">
              <input
                type={showPass ? "text" : "password"}
                value={form.pass}
                onChange={(e) => updateField("pass", e.target.value)}
                placeholder={editingId ? "Leave empty to keep old password" : "App password"}
              />
              <button
                type="button"
                className="smtp-pass-eye"
                onClick={() => setShowPass((v) => !v)}
                aria-label={showPass ? "Hide password" : "Show password"}
                title={showPass ? "Hide password" : "Show password"}
              >
                {showPass ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
          </div>

          <div className="smtp-field">
            <label>Daily Limit</label>
            <input
              type="number"
              value={form.dailyLimit}
              onChange={(e) => updateField("dailyLimit", Number(e.target.value))}
            />
          </div>

          <div className="smtp-checks">
            <label>
              <input
                type="checkbox"
                checked={form.secure}
                onChange={(e) => updateField("secure", e.target.checked)}
              />
              Use SSL Secure
            </label>

            <label>
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => updateField("active", e.target.checked)}
              />
              Active
            </label>
          </div>
        </div>

        <div className="smtp-actions">
          <button className="yellow-btn" onClick={saveSmtp} disabled={loading}>
            {loading ? "Saving..." : editingId ? "Update SMTP" : "Add SMTP"}
          </button>

          {editingId && (
            <button
              className="smtp-cancel-btn"
              onClick={() => {
                setEditingId(null);
                setForm(emptyForm);
              }}
            >
              Cancel Edit
            </button>
          )}
        </div>
      </div>

      <div className="smtp-card smtp-list-card">
        <div className="contacts-table-toolbar smtp-toolbar">
          <p>All SMTPs: {smtps.length}</p>

          <input
            className="smtp-test-input"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="Test email address"
          />
        </div>

        <MobileScrollArea className="contacts-table-scroll" ariaLabel="SMTP table">
          <table className="contacts-table smtp-table">
            <thead>
              <tr>
                <th>Sender</th>
                <th>Email</th>
                <th>Host</th>
                <th>Limit</th>
                <th>Sent Today</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {smtps.map((smtp) => (
                <tr key={smtp._id}>
                  <td>{smtp.senderName || smtp.name || "-"}</td>
                  <td>{smtp.senderEmail || "-"}</td>
                  <td>{smtp.host}:{smtp.port}</td>
                  <td>{smtp.dailyLimit}</td>
                  <td>{smtp.sentToday || 0}</td>
                  <td>
                    <span className={smtp.active ? "smtp-status active" : "smtp-status inactive"}>
                      {smtp.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className="smtp-row-actions">
                      <button onClick={() => editSmtp(smtp)}>Edit</button>
                      <button onClick={() => testSmtp(smtp._id)}>Test</button>
                      <button className="danger-outline" onClick={() => deleteSmtp(smtp._id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {smtps.length === 0 && (
                <tr>
                  <td colSpan="7" className="smtp-empty">
                    No SMTP added yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </MobileScrollArea>
      </div>
    </section>
  );
}